import React, { useState, useEffect } from 'react';
import formStyles from '../../styles/SmartSendForm.module.css';
import PhoneVerificationStep from './PhoneVerificationStep';
import CodeVerificationStep from './CodeVerificationStep';
import { isDevelopment } from '../../env/config';

/**
 * SessionReVerificationStep - Generic component for handling session expiration re-verification
 * Displays phone input modal, then code verification, then auto-retries submission
 * Used when backend returns IDENTITY_MISSING or TOKEN_EXPIRED and user is not in app (app=false)
 *
 * @param {string} phoneNumber - Current phone number value from context
 * @param {string} initialPhoneNumber - Initial phone number from session re-verification state
 * @param {Function} onPhoneChange - Handler for phone input changes
 * @param {Function} onPhoneConfirm - Handler for sending verification code
 * @param {Function} onCodeVerify - Handler for verifying entered code
 * @param {Function} onResendCode - Handler for resending verification code
 * @param {Function} onUseDifferentPhone - Handler for using a different phone number
 * @param {boolean} isLoadingCode - Loading state during code sending
 * @param {boolean} isVerifyingCode - Loading state during code verification
 * @param {boolean} isCodeVerified - Success state after verification
 * @param {string} errorType - Type of error that triggered this step ('IDENTITY_MISSING' or 'TOKEN_EXPIRED')
 * @param {boolean} isDesktop - Device type for conditional flow (desktop auto-advances, mobile shows buttons)
 * @param {string} orderId - SmartSend order identifier for scoping cooldown
 */
const SessionReVerificationStep = ({
  phoneNumber,
  initialPhoneNumber,
  onPhoneChange,
  onPhoneConfirm,
  onCodeVerify,
  onResendCode,
  onUseDifferentPhone,
  isLoadingCode = false,
  isVerifyingCode = false,
  isCodeVerified = false,
  errorType = 'IDENTITY_MISSING',
  isDesktop = false,
  orderId,
}) => {
  // Start with phone input if no phone number, otherwise show code step
  // This handles the case where session expiration occurs again after verification
  // (e.g., simulation still enabled or backend still rejecting)
  const [showCodeStep, setShowCodeStep] = useState(
    !!(phoneNumber || initialPhoneNumber)
  );
  const [localPhoneNumber, setLocalPhoneNumber] = useState(
    initialPhoneNumber || phoneNumber || ''
  );

  // Log component mount and state changes
  useEffect(() => {
    if (isDevelopment()) {
      console.log(
        `📱 [SessionReVerificationStep] Component mounted/updated (${errorType}):`,
        {
          phoneNumber,
          initialPhoneNumber,
          localPhoneNumber,
          showCodeStep,
          isLoadingCode,
          isVerifyingCode,
          isCodeVerified,
        }
      );
    }
  }, [
    phoneNumber,
    initialPhoneNumber,
    localPhoneNumber,
    showCodeStep,
    isLoadingCode,
    isVerifyingCode,
    isCodeVerified,
    errorType,
  ]);

  // Update local phone when context phone changes (after code is sent in handlePhoneConfirm)
  // This happens when onPhoneConfirm updates the context phoneNumber
  useEffect(() => {
    // Only transition to code step if phoneNumber is set AND it's different from local
    // This indicates code was successfully sent
    if (
      phoneNumber &&
      phoneNumber !== localPhoneNumber &&
      phoneNumber !== initialPhoneNumber
    ) {
      if (isDevelopment()) {
        console.log(
          `📱 [SessionReVerificationStep] Phone number updated from context, showing code step (${errorType}):`,
          {
            oldPhone: localPhoneNumber,
            newPhone: phoneNumber,
          }
        );
      }
      setLocalPhoneNumber(phoneNumber);
      setShowCodeStep(true);
    }
  }, [phoneNumber, localPhoneNumber, initialPhoneNumber, errorType]);

  /**
   * Handle phone confirmation - show code step after code is sent
   */
  const handlePhoneConfirm = async () => {
    if (isDevelopment()) {
      console.log(
        `📱 [SessionReVerificationStep] Phone confirm clicked (${errorType}):`,
        {
          phoneNumber: localPhoneNumber,
        }
      );
    }
    const success = await onPhoneConfirm();
    if (success) {
      // Code was sent successfully - transition to code step
      if (isDevelopment()) {
        console.log(
          `📱 [SessionReVerificationStep] Code sent, transitioning to code step (${errorType})`
        );
      }
      // Transition to code step immediately
      // The phoneNumber prop will be updated by parent (sessionReVerificationState.phoneNumber)
      // and the useEffect will sync localPhoneNumber
      setShowCodeStep(true);
    }
  };

  /**
   * Handle code verification - component will handle auto-retry via callback
   */
  const handleCodeVerify = async (code) => {
    if (isDevelopment()) {
      console.log(
        `📱 [SessionReVerificationStep] Code verify called (${errorType}):`,
        {
          codeLength: code.length,
          phoneNumber,
        }
      );
    }
    await onCodeVerify(code);
    // If verification succeeds, onCodeVerify callback will handle auto-retry
    // and hiding this component
  };

  /**
   * Handle using different phone - reset to phone input
   */
  const handleUseDifferentPhone = () => {
    if (isDevelopment()) {
      console.log(
        `📱 [SessionReVerificationStep] Use different phone clicked (${errorType})`
      );
    }
    setShowCodeStep(false);
    setLocalPhoneNumber('');
    if (onUseDifferentPhone) {
      onUseDifferentPhone();
    }
  };

  /**
   * Handle local phone change
   */
  const handleLocalPhoneChange = (newPhone) => {
    setLocalPhoneNumber(newPhone);
    if (onPhoneChange) {
      onPhoneChange(newPhone);
    }
  };

  return (
    <div
      className={formStyles.card}
      role="region"
      aria-label="Session verification required"
    >
      <h2 className={formStyles.sectionTitle} id="session-verification-title">
        Verification Required
      </h2>
      <p className={formStyles.infoText} id="session-verification-message">
        Your session has expired. Please verify your phone number to continue.
      </p>

      {!showCodeStep ? (
        <PhoneVerificationStep
          phoneNumber={localPhoneNumber}
          onPhoneChange={handleLocalPhoneChange}
          onConfirm={handlePhoneConfirm}
          isLoading={isLoadingCode}
          orderId={orderId}
        />
      ) : (
        <CodeVerificationStep
          phoneNumber={phoneNumber || localPhoneNumber}
          onVerify={handleCodeVerify}
          onResendCode={onResendCode}
          onUseDifferentPhone={handleUseDifferentPhone}
          isVerifyingCode={isVerifyingCode}
          isCodeVerified={false}
          isDesktop={isDesktop}
          orderId={orderId}
        />
      )}
    </div>
  );
};

export default SessionReVerificationStep;
